import { FC, useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Col, Row, Text, Icon, Content } from '@/client/components'
import { useRouterPush } from '@/client/hooks'
import { IHeader, IHeaderSidebarMenu } from '@/client/types'
import { ROUTER_PATH } from '@/client/utils'
import BottomHeader from './BottomHeader'
import Cart from './Cart'
import Login from './Login'
import Search from './Search'
import * as S from './styles'

const Header: FC<IHeader & IHeaderSidebarMenu> = ({
  header: {
    accessibility,
    banner_container,
    benefit_list,
    links,
    logo,
    calculator,
    burger,
    search,
    greet_user,
    menu_navigation,
    customer_service
  },
  setIsSideBarOpen,
  isSidebarOpen
}) => {
  const { routerPush, defaultLocale, pathname, query, asPath, push, locales } = useRouterPush()

  const handleChangeLang = useCallback(
    (lang: string) => {
      push({ pathname: pathname, query }, asPath, { locale: lang })
    },
    [pathname, asPath, query, push]
  )

  return (
    <S.Header>
      <S.SkipLink href='#menu'>{accessibility.menu}</S.SkipLink>
      <S.SkipLink href='#main'>{accessibility.main}</S.SkipLink>
      <S.SkipLink href='#footer'>{accessibility.footer}</S.SkipLink>

      {banner_container && banner_container.add_banner && banner_container.banner_content && (
        <S.BannerContainer>
          <Content content={banner_container.banner_content} inverse={true} />
        </S.BannerContainer>
      )}

      <S.TopHeaderWrapper>
        <Row>
          <Col xs={12}>
            <S.TopHeader>
              <S.TopHeaderInfos>
                {benefit_list.map(({ benefit }) => (
                  <Text key={benefit} size='xs' color='inverse' margin='0 1rem 0 0'>
                    <Icon name='checkmark' />
                    <span dangerouslySetInnerHTML={{ __html: benefit }} />
                  </Text>
                ))}
              </S.TopHeaderInfos>

              <S.TopHeaderList>
                {locales && locales.length > 1 && (
                  <>
                    {locales.includes('es') && (
                      <S.Flag onClick={() => handleChangeLang('es')} title='Español'>
                        <Icon name='spanish' />
                      </S.Flag>
                    )}
                    {locales.includes('it') && (
                      <S.Flag onClick={() => handleChangeLang('it')} title='Italiano'>
                        <Image
                          src={`/icons/italy-flag.svg`}
                          alt='Italiano'
                          width={21}
                          height={16}
                          style={{ verticalAlign: 'top', height: 'auto', marginTop: '2px' }}
                        />
                      </S.Flag>
                    )}
                    {locales.includes('nl') && (
                      <S.Flag onClick={() => handleChangeLang('nl')} title='Nederlands'>
                        <Image
                          src={`/icons/netherlands-flag.svg`}
                          alt='Nederlands'
                          width={21}
                          height={16}
                          style={{ verticalAlign: 'top', height: 'auto', marginTop: '2px' }}
                        />
                      </S.Flag>
                    )}

                    {locales.includes('fr') && (
                      <S.Flag onClick={() => handleChangeLang('fr')} title='Français'>
                        <Image
                          src={`/icons/france-flag.svg`}
                          alt='Français'
                          width={21}
                          height={16}
                          style={{ verticalAlign: 'top', height: 'auto', marginTop: '2px' }}
                        />
                      </S.Flag>
                    )}

                    <S.Flag onClick={() => handleChangeLang('en')} title='English'>
                      <Icon name='english' />
                    </S.Flag>
                  </>
                )}

                {links.map(({ link, label }) => (
                  <Link key={label} href={link} passHref legacyBehavior>
                    <S.TopHeaderLinks kind='text' size='xs' style={{ color: '#ffffff' }}>
                      {label}
                    </S.TopHeaderLinks>
                  </Link>
                ))}
              </S.TopHeaderList>
            </S.TopHeader>
          </Col>
        </Row>
      </S.TopHeaderWrapper>

      <S.MainHeader>
        <Row middle='xs'>
          <Col xs={4} sm={5} md={6} xl={2}>
            <Link href={ROUTER_PATH.HOME[defaultLocale]} title={logo.alt} passHref>
              <S.Logo>
                <Image src={logo.url} alt={logo.alt} width={logo.width} height={logo.height} />
              </S.Logo>
            </Link>
          </Col>

          <Col
            xs={12}
            xl={5}
            xlOffset={1}
            orders={[
              {
                mq: 'xs',
                order: 2
              },
              {
                mq: 'xl',
                order: 0
              }
            ]}
          >
            <Search search={search} />
          </Col>

          <Col xs={8} sm={7} md={6} xl={4} className='account-control-section'>
            <S.MainHeaderRight>
              <Login greet_user={greet_user} />
              <Cart />
              <S.HeaderButton kind='warning' onClick={() => routerPush(calculator.link)}>
                {calculator.label}
              </S.HeaderButton>
              <S.MenuButton type='button' onClick={() => setIsSideBarOpen(!isSidebarOpen)} aria-label={burger}>
                <Icon name='menu' size='s6' />
              </S.MenuButton>
            </S.MainHeaderRight>
          </Col>
        </Row>
      </S.MainHeader>
      <BottomHeader menu_navigation={menu_navigation} customer_service={customer_service} />
    </S.Header>
  )
}

export default Header
